import { useQuery } from '@tanstack/react-query';
import { bridgeApi } from '@/lib/bridgeApi';

const AUTH_STORAGE_KEY = 'patakare_auth_management';

export type ModuleVisibilityStatus = 'LIVE' | 'HIDDEN' | 'COMING_SOON' | 'BETA';

export interface ModuleConfig {
  login_enabled: boolean;
  signup_enabled: boolean;
  visibility_status?: ModuleVisibilityStatus;
  // Patient module only — see ListingCardV2.tsx's use of this. Undefined on
  // every other module.
  require_registration_to_browse?: boolean;
}

export type AuthConfig = Record<string, ModuleConfig>;

/**
 * Returns the entire auth/visibility config map.
 * Use this for navbar/category-grid filtering.
 *
 * useAuthConfig() below used to run its own separate useQuery for the exact
 * same queryKey with a different staleTime (5min vs this hook's 0) — same
 * cache entry, but react-query treats staleTime per-observer, so whichever
 * of the two mounted with staleTime:0 always considered the data stale and
 * refetched again regardless of what the other one had just fetched. Any
 * page using both hooks (e.g. a listing detail page: ListingCardV2 via one,
 * Header/Categories via the other) fired get_auth_config twice (confirmed
 * live 2026-08-20). useAuthConfig now just reads off this one query instead
 * of running its own — one shared fetch no matter which hook(s) a page uses.
 */
export function useAllModulesConfig() {
  const { data: config } = useQuery({
    queryKey: ['auth-config-check'],
    queryFn: async (): Promise<AuthConfig | null> => {
      // 1) Try Bridge API (source of truth across all devices/browsers)
      try {
        const data = await bridgeApi.call<any>('get_auth_config', {});
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          try { localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
          return data as AuthConfig;
        }
      } catch { /* fall through to localStorage */ }
      // 2) Fallback to localStorage (offline / API down)
      try {
        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (stored) return JSON.parse(stored) as AuthConfig;
      } catch { /* ignore */ }
      return null;
    },
    // Was 0 (always instantly stale) — combined with refetchOnWindowFocus and
    // 17 different components across the app calling this hook (Header,
    // Footer, Categories, one useProviderLikes-style call per ListingCardV2,
    // etc.), staleTime:0 meant every one of them mounting even slightly
    // staggered in time (as cards render in as their own data arrives, for
    // example) triggered its OWN fresh fetch instead of reading what another
    // component had just fetched moments earlier — confirmed live
    // 2026-08-20: 2-3 separate get_auth_config calls per page. A few
    // seconds is still effectively instant for a real admin toggling module
    // visibility, while covering the handful-of-hundred-ms burst every page's
    // own components mount in. refetchOnWindowFocus below still means
    // returning to an already-open tab re-checks for real changes.
    staleTime: 5000,
    refetchOnWindowFocus: true,
  });
  return config || {};
}

/**
 * Hook to check if login/signup is enabled for a specific module.
 * Used by Auth pages to enforce Admin Auth Management toggles.
 */
export function useAuthConfig(moduleKey: string) {
  const config = useAllModulesConfig();
  const moduleConfig = config?.[moduleKey];

  return {
    loginEnabled: moduleConfig?.login_enabled ?? true, // default enabled
    signupEnabled: moduleConfig?.signup_enabled ?? true,
    visibilityStatus: (moduleConfig?.visibility_status ?? 'LIVE') as ModuleVisibilityStatus,
    requireRegistrationToBrowse: moduleConfig?.require_registration_to_browse ?? true,
  };
}
